import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';

export type WizardStepKey = 'invoice' | 'terms' | 'review';

type StepDef = { key: WizardStepKey; index: number };

const DEFAULT_STEP_KEYS: WizardStepKey[] = ['invoice', 'terms', 'review'];

export type WizardStepperProps = {
  current: WizardStepKey;
  onJumpTo?: (step: WizardStepKey) => void;
  /** Steps the user has already completed (used to enable jump-back). */
  completed: Set<WizardStepKey>;
  /** Full wizard step list — defaults to all three steps. */
  visibleSteps?: WizardStepKey[];
};

/**
 * Compact horizontal stepper for the financing apply wizard.
 * Three pillars: Invoice → Terms → Review.
 */
export function WizardStepper({
  current,
  onJumpTo,
  completed,
  visibleSteps,
}: WizardStepperProps) {
  const { t } = useTranslation('m-financing');
  const STEPS: StepDef[] = (visibleSteps ?? DEFAULT_STEP_KEYS).map(
    (key, idx) => ({ key, index: idx + 1 }),
  );
  const currentIndex =
    STEPS.find(s => s.key === current)?.index ?? 1;

  return (
    <ol className="flex w-full items-stretch gap-2">
      {STEPS.map((step, i) => {
        const isCompleted = completed.has(step.key) && current !== step.key;
        const isCurrent = current === step.key;
        const isClickable =
          (isCompleted || step.index < currentIndex) && Boolean(onJumpTo);

        return (
          <li key={step.key} className="flex flex-1 items-center gap-2">
            <button
              type="button"
              disabled={!isClickable}
              onClick={() => isClickable && onJumpTo?.(step.key)}
              className={cn(
                'flex flex-1 items-center gap-3 rounded-xl border px-3 py-2 text-start transition-colors',
                isCurrent
                  ? 'border-emerald-300 bg-emerald-50/60 text-emerald-900'
                  : isCompleted
                    ? 'border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-50/40'
                    : 'border-gray-200 bg-white text-gray-500',
                isClickable ? 'cursor-pointer' : 'cursor-default',
              )}>
              <span
                className={cn(
                  'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                  isCurrent
                    ? 'bg-emerald-700 text-white'
                    : isCompleted
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-gray-100 text-gray-500',
                )}>
                {isCompleted ? <CheckIcon className="size-4" /> : step.index}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold leading-tight">
                  {t(`apply_form.steps.${step.key}.label`)}
                </div>
                <div className="hidden truncate text-xs leading-tight text-gray-500 sm:block">
                  {t(`apply_form.steps.${step.key}.description`)}
                </div>
              </div>
            </button>
            {i < STEPS.length - 1 && (
              <div
                aria-hidden
                className={cn(
                  'hidden h-px w-3 sm:block',
                  step.index < currentIndex ? 'bg-emerald-300' : 'bg-gray-200',
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
